/**
 * Beat 9's two gates are fixed-step facts, kept separate from the room so a
 * renderer cannot free Holly or open a door merely by finishing an animation.
 */

import { HOLLY_BODY_RADIUS } from '@/models/holly';
import { Companion, type CompanionObstacle, type CompanionSnapshot, type Vec2 } from '@/systems/companion';
import type { Beat, Stage, StoryContext } from '@/story/types';

export const WOOL_SHED_IDS = { pallet: 'woolShed.pallet' } as const;
export const WOOL_SHED_PUZZLE_FLAGS = { throughTheDoor: 'wool-shed.throughTheDoor' } as const;
export const WOOL_SHED_PUZZLE_CUES = { open: 'wool-shed.open', fadeOut: 'wool-shed.fadeOut', end: 'wool-shed.end' } as const;
const FADE_SECONDS = 4;

function puzzleStage(): Stage {
  return {
    id: 'puzzle',
    enter: (_ctx, emit): void => emit(WOOL_SHED_PUZZLE_CUES.open),
    // This waits on the puzzle itself. A patience clock would end beat 9 under a
    // player solving it; a companion unable to reach the latch is instead a
    // softlock whose only recovery is the pause menu restart.
    done: (ctx: StoryContext): boolean => ctx.completed.has(WOOL_SHED_PUZZLE_FLAGS.throughTheDoor),
  };
}

export const WOOL_SHED_PUZZLE_BEAT: Beat = {
  number: 9,
  id: 'act2.woolShedPuzzle',
  stages: [puzzleStage(), {
    id: 'close', enter: (_ctx, emit): void => emit(WOOL_SHED_PUZZLE_CUES.fadeOut),
    done: (ctx: StoryContext): boolean => ctx.stageTime >= FADE_SECONDS,
    exit: (_ctx, emit): void => emit(WOOL_SHED_PUZZLE_CUES.end),
  }],
};

export type PalletState = 'pinned' | 'tipped';
export type WireState = 'pinned' | 'slack';
export type BarState = 'down' | 'lifted';

export const HOLLY_TRAPPED = { x: 0.10, z: -1.55, yaw: -1.90 } as const;
/**
 * Where Holly stands to work the bar.
 *
 * **2.91 is measured, not chosen.** The bar is at z 3.30 and her reach pose puts
 * her muzzle 0.39 m in front of her root (`HOLLY_REACH_FORWARD`), so this is the
 * one z at which her nose arrives under the bar rather than short of it or
 * through the door. It is written here rather than derived because `src/story/`
 * does not import `src/world/`; `tests/e2e/wool-shed-integration.spec.ts` is
 * what holds the three numbers together, and it is a gate rather than a comment
 * for the reason D-097 gives.
 *
 * It must also stay clear of `WOOL_SHED_DOORWAY`: `Companion.step` holds
 * her still on any step Rosalie is inside a doorway, so a station inside one is
 * a station she can never be sent to.
 */
export const LATCH_STAND = { x: 0.00, z: 2.91 } as const;
/**
 * The opening Holly must not stand in, and the reason it is declared up here.
 *
 * `DOOR_APPROACH` below is derived from it rather than written out a second
 * time, so the rectangle that says "Rosalie is at the door" and the rectangle
 * that says "this is the door" cannot end up describing two different doors.
 * That is not hypothetical — see `DOOR_APPROACH`.
 */
export const WOOL_SHED_DOORWAY = { id: 'wool-shed-north-door', min: { x: -1.10, z: 3.32 }, max: { x: 1.10, z: 3.56 } } as const;
/**
 * Where Rosalie has to be standing for Holly to go and work the bar.
 *
 * **It reaches the doorway, and the 0.07 m this used to stop short of it was a
 * softlock.** The rectangle ended at z 3.25 while the shut leaf lets her walk to
 * **z 3.305** — so a player who did the one obvious thing at a closed door,
 * walked up to it and held forward, sat 55 mm past the trigger with Holly
 * holding her standoff behind and the bar never lifting. This beat has no
 * patience clock by design (see `puzzleStage`), so that state was permanent: the
 * pause-menu restart was the only way out, and the only thing on screen was a
 * shut door. The x band stopped 0.05 m short of the opening's jambs in the same
 * way, which is the same failure against the wall beside the door.
 *
 * **The three edges that touch the door come from `WOOL_SHED_DOORWAY`** — both
 * x bounds and the north one — because the honest statement is *"the whole
 * floor she can reach while the leaf is shut"* and the leaf fills the opening.
 * `min.z` is still authored at 1.85 and has nothing to derive from: it is how
 * far back "she is at the door" reaches into the room, which is a staging call
 * and not a fact about the doorway. `tests/e2e/wool-shed-integration.spec.ts` is what
 * holds it: it drives her at the shut door with forward held — never
 * `respawn` — and asserts the bar lifts. The gates that existed when this
 * shipped all teleported her to z 2.4, comfortably inside the old rectangle,
 * which is D-100 exactly: the green half was tested five times and the red half
 * not once.
 */
export const DOOR_APPROACH = {
  min: { x: WOOL_SHED_DOORWAY.min.x, z: 1.85 },
  max: { x: WOOL_SHED_DOORWAY.max.x, z: WOOL_SHED_DOORWAY.min.z },
} as const;
export const STANDOFF_RADIUS = 1.05;
export const MOVE_SPEED = 1.15;
export const SMOOTHING = 5.0;
export const ARRIVAL_RADIUS = 0.16;
export const LATCH_ARRIVAL = 0.34;
/**
 * Which way she faces to work the bar: 0, straight at the door.
 *
 * The reach pose pushes her muzzle forward along her own axis, so her heading is
 * half of where her nose ends up and the station's position is only the other
 * half. Nothing about the height measurement notices this, which is why it is
 * written down here as an authored value rather than left to whichever way she
 * happened to walk in.
 */
export const LATCH_YAW = 0;
export const RISE_SECONDS = 1.6;
export const LATCH_REACH_SECONDS = 1.9;
/**
 * How long the pallet takes to come to rest, and the bar and door to swing.
 *
 * **These are simulation values, not presentation values, and that is the whole
 * point of them being here.** The first build flipped each collider on the tick
 * the gate opened and let the mesh ease toward its new pose over render frames,
 * so for about a second the pallet's collision was leaning against the bales
 * while its picture was still flat on the floor. An independent review found it,
 * and the comment sitting next to it claimed the opposite — that swapping on the
 * fixed step was what stopped the two disagreeing.
 *
 * D-157's rule, which this module states and then broke for its own props: one
 * clock per event, published as a 0..1, and everything reads that. The collider
 * arrives when the progress does, and in between the thing is in the air and has
 * no collider — which is what a falling pallet should have.
 */
export const PALLET_FALL_SECONDS = 0.55;
export const DOOR_SWING_SECONDS = 0.9;
const TIME_EPSILON = 0.000000001;

export interface WoolShedPuzzleSnapshot {
  readonly pallet: PalletState;
  readonly wire: WireState;
  readonly bar: BarState;
  /** True exactly when the bar is lifted. The door leaf and its collider follow this. */
  readonly doorOpen: boolean;
  readonly holly: CompanionSnapshot;
  /** 0..1 while Holly is reaching for the bar; 0 in every other phase. */
  readonly latchProgress: number;
  /**
   * 0..1 as the pallet falls; 1 once it has settled, 0 while it is still pinned.
   *
   * The pallet's mesh pose AND the moment its collider appears both come from
   * this one number.
   */
  readonly palletProgress: number;
  /** The same, for the bar leaving its brackets and the door leaf swinging. */
  readonly doorProgress: number;
  /**
   * 0..1 while she is getting up out of the wire; 0 in every other phase.
   *
   * Here for the same reason `latchProgress` is: presentation needs a number for
   * exactly this interval, and the alternative is a render-side clock that
   * starts on a phase change and disagrees with this one after a dropped frame.
   * The rule the whole module is built on is that there is one clock per event
   * and it is this one.
   */
  readonly riseProgress: number;
}

export interface WoolShedPuzzleOptions {
  readonly obstacles: readonly CompanionObstacle[];
}

function inside(point: Vec2, rect: { readonly min: Vec2; readonly max: Vec2 }): boolean {
  return point.x >= rect.min.x && point.x <= rect.max.x && point.z >= rect.min.z && point.z <= rect.max.z;
}

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`wool shed puzzle ${label} must be finite`);
}

/** The authored ordering is state, not an animation callback, so it replays exactly. */
export class WoolShedPuzzle {
  readonly #obstacles: readonly CompanionObstacle[];
  #activeObstacleIds: readonly string[];
  #companion: Companion;
  #pallet: PalletState = 'pinned';
  #wire: WireState = 'pinned';
  #bar: BarState = 'down';
  #riseTime = 0;
  #latchTime = 0;
  #fallTime = 0;
  #swingTime = 0;

  constructor(options: WoolShedPuzzleOptions) {
    this.#obstacles = options.obstacles;
    this.#activeObstacleIds = this.#obstacleIds();
    this.#companion = this.#createCompanion();
  }

  #createCompanion(): Companion {
    return new Companion({
      initial: { ...HOLLY_TRAPPED, phase: 'trapped' },
      standoffRadius: STANDOFF_RADIUS,
      moveSpeed: MOVE_SPEED,
      smoothing: SMOOTHING,
      arrivalRadius: ARRIVAL_RADIUS,
      doorways: [WOOL_SHED_DOORWAY],
      stations: [{ id: 'high-latch', target: LATCH_STAND }],
      bodyRadius: HOLLY_BODY_RADIUS,
      obstacles: this.#obstacles,
    });
  }

  snapshot(): WoolShedPuzzleSnapshot {
    const holly = this.#companion.snapshot();
    return {
      pallet: this.#pallet,
      wire: this.#wire,
      bar: this.#bar,
      doorOpen: this.#bar === 'lifted',
      holly,
      latchProgress: holly.phase === 'at-high-latch' ? Math.min(1, this.#latchTime / LATCH_REACH_SECONDS) : 0,
      riseProgress: holly.phase === 'released' ? Math.min(1, this.#riseTime / RISE_SECONDS) : 0,
      palletProgress: this.#pallet === 'pinned' ? 0 : Math.min(1, this.#fallTime / PALLET_FALL_SECONDS),
      doorProgress: this.#bar === 'down' ? 0 : Math.min(1, this.#swingTime / DOOR_SWING_SECONDS),
    };
  }

  reset(): void {
    this.#companion = this.#createCompanion();
    this.#pallet = 'pinned';
    this.#wire = 'pinned';
    this.#bar = 'down';
    this.#riseTime = 0;
    this.#latchTime = 0;
    this.#fallTime = 0;
    this.#swingTime = 0;
    this.#activeObstacleIds = this.#obstacleIds();
  }

  step(dt: number, rosalie: Vec2, completed: ReadonlySet<string>): WoolShedPuzzleSnapshot {
    // Validate before gate one writes: a NaN from the host must not half-release
    // Holly and leave a later valid replay starting from an impossible state.
    finite(dt, 'step dt');
    if (dt < 0) throw new Error('wool shed puzzle step dt must not be negative');
    finite(rosalie.x, 'rosalie.x');
    finite(rosalie.z, 'rosalie.z');

    if (this.#pallet === 'pinned' && completed.has(WOOL_SHED_IDS.pallet)) {
      this.#pallet = 'tipped';
      this.#wire = 'slack';
      if (!this.#companion.release()) throw new Error('wool shed puzzle could not release trapped Holly');
      this.#activeObstacleIds = this.#obstacleIds();
    }
    if (this.#pallet === 'tipped' && this.#fallTime < PALLET_FALL_SECONDS) {
      this.#fallTime += dt;
      if (this.#fallTime >= PALLET_FALL_SECONDS) this.#activeObstacleIds = this.#obstacleIds();
    }
    if (this.#bar === 'lifted' && this.#swingTime < DOOR_SWING_SECONDS) {
      this.#swingTime += dt;
      // The open leaf becomes solid to her on the tick its collider is created.
      if (this.#swingTime >= DOOR_SWING_SECONDS) {
        this.#activeObstacleIds = this.#obstacleIds();
        if (this.#companion.snapshot().phase === 'opening-door' && !this.#companion.resumeFollowing()) {
          throw new Error('wool shed puzzle could not resume following after door swing');
        }
      }
    }

    const before = this.#companion.snapshot();
    if (before.phase === 'released') {
      this.#riseTime += dt;
      if (this.#riseTime >= RISE_SECONDS - TIME_EPSILON && !this.#companion.beginFollowing()) {
        throw new Error('wool shed puzzle could not begin following after rise');
      }
    }

    // The high-latch station exists only while the bar is down. Leaving this
    // true after opening made a resumed `following` companion keep selecting
    // the old station instead of using her normal standoff, so she remained at
    // the doorway she had just opened.
    const atLatchApproach = this.#bar === 'down' && inside(rosalie, DOOR_APPROACH);
    this.#companion.step(dt, rosalie, {
      atStation: atLatchApproach,
      obstacleIds: this.#activeObstacleIds,
      ...(atLatchApproach ? { stationIds: ['high-latch'] } : {}),
    });

    const holly = this.#companion.snapshot();
    if (this.#bar === 'down' && holly.phase === 'following' && atLatchApproach
      && Math.hypot(holly.x - LATCH_STAND.x, holly.z - LATCH_STAND.z) <= LATCH_ARRIVAL) {
      if (!this.#companion.beginHighLatch()) throw new Error('wool shed puzzle could not begin high latch');
      // Turn her to the door. She arrives facing wherever she walked in from,
      // and the reach carries her muzzle forward along her OWN axis — so
      // without this she rears at the wall beside the bar. See `Companion.face`.
      this.#companion.face(LATCH_YAW);
    }

    if (this.#companion.snapshot().phase === 'at-high-latch' && this.#bar === 'down') {
      this.#latchTime += dt;
      if (this.#latchTime >= LATCH_REACH_SECONDS - TIME_EPSILON) {
        if (!this.#companion.beginOpeningDoor()) throw new Error('wool shed puzzle could not open door from latch');
        this.#bar = 'lifted';
        this.#latchTime = 0;
        // The bar and the leaf stop being solid to her on the same edge that
        // removes their Rapier colliders in `main.ts`. See `#obstacleIds`.
        this.#activeObstacleIds = this.#obstacleIds();
      }
    }
    return this.snapshot();
  }

  /**
   * Which solids are solid TO HER right now — and the rule is one sentence:
   * **this list mirrors the Rapier colliders `main.ts` maintains**, event for
   * event, because both are derived from this snapshot's clocks (D-157). The
   * alternative is two hand-maintained lists of names that agree until somebody
   * edits one, which is the failure `solids.ts` exists to prevent and which this
   * beat has already paid for twice.
   *
   * It FILTERS the list it was given rather than naming a fixed set, because the
   * height band in `woolShedCompanionObstacles` legitimately drops solids — the
   * pinned pallet is 0.14 m and she steps over it, so it is not in the list at
   * all — and `Companion.step` throws on an id the config does not know.
   */
  #obstacleIds(): readonly string[] {
    return this.#obstacles.filter((obstacle) => {
      // In the air it has no collider, and that is deliberate: `main.ts` creates
      // its Rapier box on this same `palletProgress >= 1` condition.
      if (obstacle.id === 'pallet-tipped') return this.#pallet === 'tipped' && this.#fallTime >= PALLET_FALL_SECONDS;
      // Gone on the edge the pallet tips, exactly as its collider is removed.
      if (obstacle.id === 'pallet-pinned') return this.#pallet !== 'tipped';
      // Arrives when the swing finishes, exactly as its collider does. Without
      // this she was the one body in the room that could walk through a door
      // standing wide open; an independent review found it, and the comment
      // right here claimed the mirror was complete while it was not (D-097).
      if (obstacle.id === 'door-leaf-open') return this.#bar === 'lifted' && this.#swingTime >= DOOR_SWING_SECONDS;
      // Both go when the bar lifts, exactly as both colliders do.
      //
      // They stay solid until then, and the margin that allows it is thin enough
      // to write down: `LATCH_STAND` stands 0.53 m clear of the leaf's face and
      // **0.345 m clear of the bar's footprint**, against her 0.30 m radius. That
      // 45 mm is the whole slack in the climax of this beat, which is why
      // `wool-shed.spec.ts` measures it and `Companion`'s constructor throws on a
      // station its radius has swallowed — a boot error rather than a door she
      // walks up to forever.
      if (obstacle.id === 'drop-bar' || obstacle.id === 'door-leaf') return this.#bar === 'down';
      return true;
    }).map((obstacle) => obstacle.id);
  }
}
